import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from '../../shared/Toast';
import { Icon } from '../BaileyBits';
import { when } from '../bailey/format';
import { openProposedBooking } from '../bailey/baileyBooking';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import MessagePanel from './MessagePanel';
import ReplyPanel from './ReplyPanel';
import AnswerPanel from './AnswerPanel';
import { BaileyStill } from '../bailey/BaileyAvatar';
import { dialable } from './api';
import { prepareBooking, prepareConsultation, useNamedBooking } from './bookIt';
import {
    needsFrom, needsLine, nextStep, stepLabel, groupTitle, questionFrom, isEarlier, describeBooking, reachOf,
    KINDS, sortKinds, ofKind, briefOf, BRIEF_OVER
} from './needs';

/**
 * Needs you, for the messages Bailey took: one row per person, the most
 * pressing first, each with the next step Bailey has ready. Tapping a row
 * opens everything about that person, down to the conversation itself.
 *
 * Used on the Bailey home and the front desk page (a few rows and See all)
 * and on Conversations › Messages (everyone, as cards).
 */

/** The people waiting, ranked, from the front desk's open messages. */
export function useNeeds(fd) {
    const open = fd ? fd.open : null;
    return useMemo(() => needsFrom(open || []), [open]);
}

/**
 * What every surface can do with a person: open them, book, reply, teach
 * Bailey, call, mark done with Undo. Returns the panels and the Undo bar to
 * render.
 */
export function useNeedActions({ fd, groups, history, salonName }) {
    const [panel, setPanel] = useState(null);   // { kind: 'person' | 'reply' | 'answer', key }
    const [busy, setBusy] = useState(null);     // { key, what: 'book' | 'consult' }
    const [undo, setUndo] = useState(null);     // { ids, label }
    const undoTimer = useRef(null);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; clearTimeout(undoTimer.current); }, []);

    // Panels follow the group by key, so a person's panel keeps up as their
    // messages change, and goes when they're done.
    const byKey = useMemo(() => (groups || []).reduce((o, g) => { o[g.key] = g; return o; }, {}), [groups]);
    const current = panel && byKey[panel.key];
    // A reply or answer panel keeps the person it opened with.
    const held = useRef(null);
    if (current) held.current = current;

    const done = useCallback((ids, label) => {
        if (!fd || !ids.length) return;
        fd.markMessageDone(ids);
        hapticSuccess();
        clearTimeout(undoTimer.current);
        setUndo({ ids, label });
        undoTimer.current = setTimeout(() => { if (alive.current) setUndo(null); }, 8000);
    }, [fd]);

    const doneGroup = useCallback((g) => {
        const whose = g.name ? `${g.name}’s` : 'Their';
        done(g.ids, g.count === 1 ? `${whose} message is done` : `${whose} ${g.count} messages are done`);
    }, [done]);

    const runUndo = () => {
        if (!undo) return;
        hapticTick();
        clearTimeout(undoTimer.current);
        fd.markMessageOpen(undo.ids);
        setUndo(null);
    };

    // The booking Bailey worked out, into the diary's own panel.
    const handOff = async (g, b, what) => {
        if (busy) return;
        hapticTick();
        setBusy({ key: g.key, what });
        try {
            const person = { name: g.name, phone: g.phone, email: g.email };
            const card = what === 'consult' ? await prepareConsultation(b, person) : await prepareBooking(b, person);
            if (!alive.current) return;
            setBusy(null);
            if (!card) { hapticError(); toast.error('I couldn’t find the consultation service for that stylist. Book it from the diary instead.'); return; }
            if (!card.services.length) { hapticError(); toast.error('That stylist doesn’t offer those services any more. Book it from the diary instead.'); return; }
            if (card.missing) toast.info('One of the services has changed since, so check the booking before you save it.');
            openProposedBooking(card, history);
        } catch (e) {
            if (!alive.current) return;
            setBusy(null);
            hapticError();
            toast.error('I couldn’t get that booking ready just now. Try again.');
        }
    };

    const act = {
        open: (g) => { hapticTick(); setPanel({ kind: 'person', key: g.key }); },
        reply: (g) => { hapticTick(); setPanel({ kind: 'reply', key: g.key }); },
        answer: (g) => { hapticTick(); setPanel({ kind: 'answer', key: g.key }); },
        book: (g, b) => handOff(g, b, 'book'),
        consult: (g, b) => handOff(g, b, 'consult'),
        done: doneGroup,
        doneMany: done,
        busyFor: (g) => (busy && busy.key === g.key ? busy.what : null)
    };

    const close = () => { setPanel(null); held.current = null; };
    const g = current || (panel && panel.kind !== 'person' ? held.current : null);
    let panels = null;
    if (panel && g && fd) {
        if (panel.kind === 'person') {
            panels = (
                <MessagePanel group={g} fd={fd} busy={act.busyFor(g)} onBook={act.book} onConsult={act.consult}
                    onReply={act.reply} onAnswer={act.answer} onDone={doneGroup} onClose={close} />
            );
        } else if (panel.kind === 'reply') {
            panels = <ReplyPanel group={g} answers={fd.answers} salonName={salonName} onDone={doneGroup} onTeach={act.answer} onClose={close} />;
        } else if (panel.kind === 'answer') {
            panels = (
                <AnswerPanel
                    question={questionFrom((g.messages.find((m) => m.text) || g.latest).text)}
                    answers={fd.answers}
                    onAdd={({ question, answer }) => fd.addAnswer(question, answer)}
                    onChange={(old, next) => fd.changeAnswer(old, next)}
                    onRemove={(old) => fd.removeAnswer(old._id)}
                    onClose={close}
                />
            );
        }
    }
    const undoBar = undo && (
        <div className="fd-undo" role="status">
            <span>{undo.label}</span>
            <button type="button" onClick={runUndo}>Undo</button>
        </div>
    );
    return { act, panels, undoBar };
}

const STEP_ICONS = { book: Icon.Calendar, reply: Icon.Mail, email: Icon.Mail, call: Icon.Phone, answer: Icon.Chat, look: Icon.Chat };

/** The next step as a button: a phone link for a call, otherwise Bailey's own action. */
function StepButton({ g, step, act, className }) {
    const busy = act.busyFor(g);
    if (step.kind === 'call') {
        return <a className={className} href={`tel:${dialable(g.phone)}`} onClick={() => hapticTick()}>{stepLabel(step)}</a>;
    }
    const run = () => {
        if (step.kind === 'book') act.book(g, step.booking);
        else if (step.kind === 'reply' || step.kind === 'email') act.reply(g);
        else if (step.kind === 'answer') act.answer(g);
        else act.open(g);
    };
    return (
        <button type="button" className={className} disabled={!!busy} onClick={run}>
            {busy ? 'Getting it ready…' : stepLabel(step)}
        </button>
    );
}

const gist = (text, max = 110) => {
    const t = String(text || '').trim().replace(/\s+/g, ' ');
    return t.length <= max ? t : `${t.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
};

/** One person, as a row: who, how many, what they said, and the next step. */
export function NeedRow({ g, step, act }) {
    const I = STEP_ICONS[step.kind] || Icon.Chat;
    return (
        <div className={`fd-need${g.unhappy ? ' is-unhappy' : ''}`}>
            <button type="button" className="fd-need-open" onClick={() => act.open(g)} aria-label={`${groupTitle(g, step)}. Open`}>
                <span className="fd-need-tile" aria-hidden="true"><I size={16} /></span>
                <span className="fd-need-copy">
                    <b>
                        <span className="fd-need-name">{g.name || 'A client'}</span>
                        {g.count > 1 && <em>{g.count}</em>}
                        {g.unhappy && <span className="fd-unhappy">Unhappy</span>}
                    </b>
                    <small className="fd-need-gist">{gist(g.latest.text, 160)}</small>
                    <small className="fd-need-when">{g.count > 1 ? `Since ${sinceLabel(g.since)}` : when(g.at)}</small>
                </span>
            </button>
            <StepButton g={g} step={step} act={act} className={`bly-btn ${step.kind === 'answer' || step.kind === 'look' ? 'is-secondary' : 'is-primary'} fd-need-go`} />
        </div>
    );
}

const sinceLabel = (at) => { const w = when(at); return w === 'Yesterday' ? 'yesterday' : w === 'Just now' ? 'just now' : w; };

/**
 * A few people, most pressing first, and See all. On the Bailey home under
 * Needs you, and on the front desk page.
 */
export function NeedsList({ fd, groups, limit = 3, onSeeAll, act }) {
    const shown = groups.slice(0, Math.max(1, limit));
    // Their conversations, in the background, so a booking in one is ready
    // to book. An unhappy client gets a call whatever they asked for.
    const want = shown.filter((g) => !g.unhappy).flatMap((g) => g.sessionIds).join(',');
    useEffect(() => { if (fd && want) fd.wantConversations(want.split(',')); }, [fd && fd.wantConversations, fd && fd.conversations, want]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!shown.length) return null;
    const total = groups.reduce((n, g) => n + g.count, 0);
    return (
        <div className="bly-card bly-list fd-needs">
            {shown.map((g) => <NeedRow key={g.key} g={g} step={nextStep(g, fd.conversationsById)} act={act} />)}
            {groups.length > shown.length && onSeeAll && (
                <button type="button" className="bly-list-more" onClick={() => { hapticTick(); onSeeAll(); }}>See all {total}</button>
            )}
        </div>
    );
}

/** The summary line above the rows. */
export const NeedsLine = ({ groups }) => {
    const line = needsLine(groups);
    return line ? <p className="fd-needs-line">{line}</p> : null;
};

/** One person on the full list: their latest message in full, the rest folded, and every action. */
export function NeedCard({ g, step, act }) {
    const [more, setMore] = useState(false);
    const I = STEP_ICONS[step.kind] || Icon.Chat;
    const b = useNamedBooking(step.kind === 'book' ? step.booking : null);
    const reach = reachOf(g);
    const meta = [g.count > 1 ? `${g.count} messages, latest ${when(g.at)}` : when(g.at), reach || 'No contact details'].join(' · ');
    const canReply = !!g.email || (!!g.phone && g.question);
    const others = [
        g.phone && step.kind !== 'call' && <a key="call" className="bly-btn is-secondary" href={`tel:${dialable(g.phone)}`} onClick={() => hapticTick()}>Call</a>,
        canReply && step.kind !== 'reply' && step.kind !== 'email' && <button key="reply" type="button" className="bly-btn is-secondary" onClick={() => act.reply(g)}>Reply</button>
    ].filter(Boolean);
    return (
        <div className={`bly-card fd-person-card${g.unhappy ? ' is-unhappy' : ''}`}>
            <button type="button" className="fd-person-open" onClick={() => act.open(g)}>
                <span className="fd-need-tile" aria-hidden="true"><I size={16} /></span>
                <span className="fd-person-copy">
                    <b>{groupTitle(g, step)}{g.unhappy && <span className="fd-unhappy">Unhappy</span>}</b>
                    <small>{meta}</small>
                </span>
                <span className="bly-chev" aria-hidden="true"><Icon.Chevron /></span>
            </button>
            <p className="fd-person-text">{g.latest.text}</p>
            <div className="fd-person-links">
                {g.count > 1 && (
                    <button type="button" className="fd-inline-link" aria-expanded={more} onClick={() => { hapticTick(); setMore((v) => !v); }}>
                        {more ? 'Hide the earlier messages' : `Show ${g.count - 1} earlier ${g.count === 2 ? 'message' : 'messages'}`}
                    </button>
                )}
                <button type="button" className="fd-inline-link" onClick={() => act.open(g)}>See the conversation</button>
            </div>
            {more && (
                <div className="fd-msgs is-earlier">
                    {g.messages.slice(1).map((m) => (
                        <div key={m.id} className="fd-msg"><p>{m.text || 'No message.'}</p><small>{when(m.at)}</small></div>
                    ))}
                </div>
            )}
            {b && (
                <p className="fd-person-ready"><span>Ready for you</span>{describeBooking(b)}. Book it and your diary opens filled in, for you to save.</p>
            )}
            <div className="bly-actions fd-person-actions">
                <button type="button" className="bly-btn is-ghost" onClick={() => act.done(g)}>Done</button>
                {others}
                <StepButton g={g} step={step} act={act} className={`bly-btn ${step.kind === 'look' ? 'is-secondary' : 'is-primary'}`} />
            </div>
        </div>
    );
}

/** Bailey's line at the top of a long list: where to start, then how the rest break down. */
export function BaileyBrief({ look, text }) {
    return (
        <div className={`fd-brief${text ? '' : ' is-reading'}`} aria-live="polite">
            <span className="fd-brief-face" aria-hidden="true"><BaileyStill appearance={look} face /></span>
            <div className="fd-brief-copy">
                <small>Bailey’s summary</small>
                <p>{text || 'Reading through the conversations…'}</p>
            </div>
        </div>
    );
}

// Worth showing when there are at least two kinds to choose between, or one is chosen.
const wantsFilters = (kinds, kind) => kind !== 'all' || KINDS.filter((k) => kinds.counts[k.id] > 0).length > 1;

/**
 * Filter chips: everyone, then each kind with someone in it (and the one
 * chosen, even once it empties). The red one is the Unhappy tag on the cards.
 */
export function NeedsFilters({ kinds, total, kind, onKind }) {
    const chip = (id, label, n, extra = '') => (
        <button key={id} type="button" className={`fd-kind${extra}`} aria-pressed={kind === id} aria-label={`${label}, ${n}`}
            onClick={() => { if (kind !== id) { hapticTick(); onKind(id); } }}>
            {label}<span className="fd-kind-n">{n}</span>
        </button>
    );
    return (
        <div className="fd-kinds" role="group" aria-label="Filter the messages">
            {chip('all', 'All', total)}
            {KINDS.filter((k) => kinds.counts[k.id] > 0 || k.id === kind).map((k) => chip(k.id, k.label, kinds.counts[k.id], k.id === 'unhappy' ? ' is-unhappy' : ''))}
        </div>
    );
}

/**
 * Everyone waiting, as cards: the last week first, then Earlier. Above them,
 * Bailey's summary when more than a handful are waiting, and filters by what
 * they're waiting for. Mark all done (with Undo) clears what's shown.
 */
export function NeedsCards({ fd, groups, act, undoBar, searching, kind = 'all', onKind, look }) {
    const want = groups.filter((g) => !g.unhappy).flatMap((g) => g.sessionIds).join(',');
    useEffect(() => { if (fd && want) fd.wantConversations(want.split(',')); }, [fd && fd.wantConversations, fd && fd.conversations, want]); // eslint-disable-line react-hooks/exhaustive-deps
    const kinds = sortKinds(groups, fd.conversationsById);
    const shown = ofKind(groups, kinds, kind);
    const filtered = kind !== 'all';
    // Every conversation they're waiting on is in (or can't be found), so the
    // summary won't change once it's said.
    const settled = groups.every((g) => g.unhappy || g.sessionIds.every((id) => fd.conversationSettled(id)));
    const brief = !searching && groups.length > BRIEF_OVER;
    const recent = shown.filter((g) => !isEarlier(g));
    const earlier = shown.filter((g) => isEarlier(g));
    const ids = shown.reduce((all, g) => all.concat(g.ids), []);
    const markAll = () => act.doneMany(ids, ids.length === 1 ? '1 message is done' : `${ids.length} messages are done`);
    const card = (g) => <NeedCard key={g.key} g={g} step={kinds.steps[g.key]} act={act} />;
    const none = KINDS.find((k) => k.id === kind);
    const emptyText = searching ? 'Nothing here matches your search.' : kind === 'book' && !settled ? 'Still checking the conversations for bookings.' : none ? none.none : '';
    const filters = !!onKind && !!groups.length && wantsFilters(kinds, kind);
    return (
        <>
            {(brief || filters || !!shown.length || !!undoBar) && (
                <div className="fd-needs-top">
                    {brief && <BaileyBrief look={look} text={settled ? briefOf(groups, kinds) : ''} />}
                    {filters && <NeedsFilters kinds={kinds} total={groups.length} kind={kind} onKind={onKind} />}
                    {(!!shown.length || !!undoBar) && (
                        <div className="fd-cards-head">
                            <NeedsLine groups={shown} />
                            {!!shown.length && (
                                <button type="button" className="bly-btn is-ghost fd-mark-all" onClick={() => { hapticTick(); markAll(); }}>
                                    {searching || filtered ? 'Mark these done' : 'Mark all done'}
                                </button>
                            )}
                        </div>
                    )}
                    {undoBar}
                </div>
            )}
            {/* Keyed by the filter, so a new choice rises in like the page did. */}
            <div key={kind} className="fd-cards-body">
                {filtered && !shown.length && !!groups.length && <p className="fd-empty">{emptyText}</p>}
                {!!recent.length && <div className="bly-stack">{recent.map(card)}</div>}
                {!!earlier.length && (
                    <section className="bly-sec">
                        <div className="bly-sec-head"><h2 className="bly-label">Earlier</h2></div>
                        <div className="bly-stack">{earlier.map(card)}</div>
                    </section>
                )}
            </div>
        </>
    );
}
